import { useCallback } from "react";
import {
  getNextMonthStartAndEndDate,
  getNextWeekStartAndEndDate,
  getThisMonthStartAndEndDate,
  getThisWeekendStartAndEndDate,
  getTodayStartAndEndDate,
  getTomorrowStartAndEndDate,
} from "@shared/utils/dates/get-dates/get-dates-timezone";
import { useBrowserTimezone } from "@shared/hooks/useBrowserTimezone";
import { useTranslation } from "@shared/utils/internationalization";
import {
  DatePickerSelectionEvent,
  DatePickerMode,
  BASE_SELECTION_EVENT,
  DatePickerProps,
  DatePickerSelection,
} from "../../DatePicker/DatePicker.props";

type DatePickerQuickOptions = {
  setSelection: (selected: DatePickerSelection) => void;
  mode: DatePickerProps["mode"];
  onChangeSelected?: (event: DatePickerSelectionEvent) => void;
  setMonth?: (date: Date) => void;
};

export default function useDatePickerQuickOptions({
  setSelection,
  mode,
  onChangeSelected,
  setMonth,
}: DatePickerQuickOptions) {
  const { t } = useTranslation("common");

  const timezone = useBrowserTimezone();

  // Transform selected dates to start and end dates based on the date picker mode
  const processSelection = useCallback(
    ({ startDate, endDate }: { startDate: Date; endDate: Date }) => {
      const update: DatePickerSelectionEvent = {
        ...BASE_SELECTION_EVENT,
      };

      if (mode === DatePickerMode.range) {
        update.selected = { from: startDate, to: endDate };
      }

      if (mode === DatePickerMode.multiple) {
        update.selected = [startDate, endDate];
      }

      if (mode === DatePickerMode.single) {
        update.selected = startDate;
      }

      setSelection(update.selected);
      setMonth?.(startDate);
      onChangeSelected?.(update);
    },
    []
  );

  // Get preset selections for a set of date ranges
  const dateQuickMenuOptions = [
    {
      title: t("Today"),
      onClick: () => processSelection(getTodayStartAndEndDate(timezone)),
    },
    {
      title: t("Tomorrow"),
      onClick: () => processSelection(getTomorrowStartAndEndDate(timezone)),
    },
    {
      title: t("This Weekend"),
      onClick: () => processSelection(getThisWeekendStartAndEndDate(timezone)),
    },
    {
      title: t("Next Week"),
      onClick: () => processSelection(getNextWeekStartAndEndDate(timezone)),
    },
    {
      title: t("This Month"),
      onClick: () => processSelection(getThisMonthStartAndEndDate(timezone)),
    },
    {
      title: t("Next Month"),
      onClick: () => processSelection(getNextMonthStartAndEndDate(timezone)),
    },
  ];

  return dateQuickMenuOptions;
}
